import React from 'react';
import { useForm } from '../../context/FormContext';
import { useAppConfig } from '../../context/AppConfigContext';
import { useUpdateMode } from '../../hooks/useUpdateMode';
import { BreedPhoto } from './BreedPhoto';

interface PetsSummaryBarProps {
  /** When provided, desktop pills become clickable buttons opening the drawer for that pet. */
  onPillClick?: (petId: string) => void;
}

const GENERIC_BREED_KEYS = new Set(['mestizo_cruce', 'mestizo', 'no_lo_se']);

const PetsSummaryBar: React.FC<PetsSummaryBarProps> = ({ onPillClick }) => {
  const { petsCompleted, formData } = useForm();
  const { config } = useAppConfig();
  const { isUpdateMode } = useUpdateMode();

  function breedLabel(key: string | null | undefined): string | null {
    if (!key || GENERIC_BREED_KEYS.has(key)) return null;
    return config.breed_database?.breeds?.[key]?.display_name ?? null;
  }

  if (petsCompleted.length === 0 || isUpdateMode) return null;

  const currentNombre = (formData.nombre || '').trim();
  const currentRaza = formData.raza || undefined;
  const showCurrent = currentNombre.length > 0;

  return (
    <div className="w-full bg-blue-50 border-b border-blue-100 px-4 py-1.5 flex items-center gap-2 flex-wrap flex-shrink-0">
      <span className="text-xs font-semibold text-[#008BBC] uppercase tracking-wide mr-1">En tu pedido:</span>

      {petsCompleted.map((pet) => (
        <React.Fragment key={pet.id}>
          {/* Mobile: clickable pill (no delete to avoid accidental removes) */}
          <button
            type="button"
            onClick={() => onPillClick?.(pet.id)}
            className="lg:hidden inline-flex items-center gap-1.5 bg-white border border-blue-200 rounded-full pl-1.5 pr-2.5 py-0.5 text-xs font-medium text-gray-700 hover:bg-blue-50 transition-colors"
          >
            <BreedPhoto breedKey={pet.raza} className="w-5 h-5 flex-shrink-0" alt="" />
            <span>{pet.nombre}</span>
          </button>

          <span className="hidden lg:inline-flex items-center">
            <button
              type="button"
              onClick={() => onPillClick?.(pet.id)}
              className="inline-flex items-center gap-1.5 bg-white border border-blue-200 rounded-full pl-1.5 pr-2.5 py-0.5 text-xs font-medium text-gray-700 hover:bg-blue-50 transition-colors"
            >
              <BreedPhoto breedKey={pet.raza} className="w-5 h-5 flex-shrink-0" alt="" />
              <span>{pet.nombre}</span>
              {breedLabel(pet.raza) && (
                <>
                  <span className="text-gray-300">·</span>
                  <span className="text-gray-500">{breedLabel(pet.raza)}</span>
                </>
              )}
            </button>
          </span>
        </React.Fragment>
      ))}

      {showCurrent && (
        <button
          type="button"
          onClick={() => onPillClick?.('current')}
          className="inline-flex items-center gap-1.5 bg-white border border-blue-100 rounded-full pl-1.5 pr-2.5 py-0.5 text-xs font-medium text-gray-600 hover:bg-blue-50 transition-colors"
        >
          <BreedPhoto breedKey={currentRaza} className="w-5 h-5 flex-shrink-0" alt="" />
          <span>{currentNombre}</span>
        </button>
      )}
    </div>
  );
};

export default PetsSummaryBar;
